import { DESIGN_AREA_PROPERTY, type Scene } from '@pz/scene';
import { elementSpot, expandedZoneLayers, type GuideSpot } from './guide.js';
import * as layers from './layers.js';
import type { ProductDefinition, Values } from './types.js';

/**
 * "What am I editing right now": a small always-on highlight, independent of
 * the optional teddy-bear guide.
 *
 * The guide explains and narrates; this is quieter and always there (when the
 * shop has not turned the guide's outlines off, the two coincide on purpose —
 * see the shared outline colour in styles.ts). It answers one question only:
 * which question is the shopper's cursor in, right now, and what does it draw
 * on the product. A chip names the question, and a plain outline sits on every
 * visible layer that question drives.
 */

export interface FocusSpotTexts {
  /** The chip's leading word. Default "Uređuješ" (a merchant can override it
   *  through `display.labels.editing`). */
  editing?: string;
  /** The same word for a SUGGESTED question — one nobody has touched yet.
   *  Default "Dodaj" (overridable through `display.labels.add`). */
  add?: string;
}

export interface FocusSpotOptions {
  /** The form element; rows and (nested) song lines carry `data-option`. */
  form: HTMLElement;
  /** The box the canvas sits in; the chip and every outline are positioned inside it. */
  previewBox: HTMLElement;
  /** The preview canvas, wherever it currently sits (page, wizard pane, lightbox). */
  canvas: HTMLElement;
  /** The resolved scene actually painted, with placements and visibility already applied. */
  scene: () => Scene | undefined;
  values: () => Values;
  product: ProductDefinition;
  /** True while the canvas is inside the full-screen lightbox (outlines are suppressed there). */
  canvasInLightbox: () => boolean;
  texts?: FocusSpotTexts;
}

export interface FocusSpot {
  /** The preview painted, moved or resized: realign to the painted scene. */
  relayout(): void;
  /**
   * Point at a question nobody has focused yet — "start here".
   *
   * The same row class, the same outlines, the same chip, only its leading word
   * reads "Dodaj" instead of "Uređuješ". It is a suggestion, not a focus: the
   * first real `focusin`/`pointerdown` anywhere in the form replaces it, and a
   * `focusout` never clears it, because nothing ever had focus. Nothing is
   * focused programmatically — a phone keyboard flying up over the product is
   * not help.
   *
   * `null` withdraws a suggestion (and leaves a real focus alone).
   * `onlyWhenIdle` skips the whole thing while the shopper's cursor is inside
   * some question: an answer landing must never move the highlight off the
   * field they are still typing in.
   */
  suggest(optionId: string | null, opts?: { onlyWhenIdle?: boolean }): void;
  /** Removes the chip and every outline, without unbinding listeners. */
  clear(): void;
  /** Removes everything and unbinds every listener. */
  destroy(): void;
}

// ── Pure helpers, exported for tests ─────────────────────────────────────────

export interface OptionHit {
  /** The nearest ancestor (inclusive) carrying `data-option` — a `.pz-row` or,
   *  nested inside one, something narrower like a `.pz-song-line`. */
  el: HTMLElement;
  optionId: string;
}

/** The `[data-option]` an event happened in, without `closest()`. */
export function optionOfNode(node: unknown): OptionHit | null {
  let el = node as HTMLElement | null;
  let depth = 0;
  while (el && depth < 24) {
    const id = el.getAttribute?.('data-option');
    if (id) return { el, optionId: id };
    el = el.parentElement ?? null;
    depth += 1;
  }
  return null;
}

/**
 * Every bound, visible layer one option currently drives on the painted
 * scene — the element ids to outline.
 *
 * Mirrors guide.ts's `boundElements`, as a pure function: a `visibility`
 * binding changes whether a layer is drawn at all rather than what it looks
 * like, so it never gets its own outline; a choice-scoped binding
 * (`optionValueId`) counts only when that choice is the one currently picked;
 * a design area is never itself on the painted scene — it was replaced by the
 * shopper's own layers — so it counts as present once at least one of them
 * is, and `elementSpot`'s zone fallback turns the zone id back into a box
 * around them.
 */
export function spotElementsFor(
  product: ProductDefinition,
  scene: Scene | undefined,
  values: Values,
  optionId: string,
): string[] {
  if (!scene) return [];
  const option = product.options?.find((o) => o.id === optionId);
  const answer = (values ?? {})[optionId];
  const answers = Array.isArray(answer) ? answer : [answer];
  const picked = new Set(
    (option?.values ?? []).filter((v) => answers.includes(v.value)).map((v) => v.id),
  );
  const found = new Set<string>();
  for (const binding of product.bindings ?? []) {
    if (binding.optionId !== optionId) continue;
    if (binding.property === 'visibility') continue;
    if (binding.optionValueId && !picked.has(binding.optionValueId)) continue;
    if (binding.property === DESIGN_AREA_PROPERTY) {
      if (expandedZoneLayers(scene, binding.elementId).length === 0) continue;
      found.add(binding.elementId);
      continue;
    }
    const element = scene.elements?.find((el) => el.id === binding.elementId);
    if (!element?.transform || element.visible === false || element.printOnly) continue;
    found.add(element.id);
  }
  return [...found];
}

/**
 * The chip's sentence: "Uređuješ: <label>" while the question has the cursor,
 * "Dodaj: <label>" while it is only being suggested. Both leading words are
 * overridable.
 */
export function chipText(
  texts: FocusSpotTexts | undefined,
  label: string,
  kind: 'editing' | 'add' = 'editing',
): string {
  const lead =
    kind === 'add' ? texts?.add?.trim() || 'Dodaj' : texts?.editing?.trim() || 'Uređuješ';
  return `${lead}: ${label}`;
}

// ── The shell ────────────────────────────────────────────────────────────────

interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
  right: number;
  bottom: number;
}

const EMPTY_BOX: Box = { left: 0, top: 0, width: 0, height: 0, right: 0, bottom: 0 };

function rectOf(el: Element | null | undefined): Box {
  const rect = (el as HTMLElement | null | undefined)?.getBoundingClientRect?.();
  if (!rect || typeof rect.width !== 'number') return EMPTY_BOX;
  const left = rect.left ?? 0;
  const top = rect.top ?? 0;
  const width = rect.width ?? 0;
  const height = rect.height ?? 0;
  return { left, top, width, height, right: rect.right ?? left + width, bottom: rect.bottom ?? top + height };
}

function make(tag: string, className: string): HTMLElement {
  const el = document.createElement(tag) as HTMLElement;
  el.className = className;
  return el;
}

function safe<T>(fn: () => T, fallback: T): T {
  try {
    const value = fn();
    return value === undefined ? fallback : value;
  } catch {
    return fallback;
  }
}

/** The active question's own highlight, and the chip that names it. */
export function createFocusSpot(options: FocusSpotOptions): FocusSpot {
  const spots = new Map<string, HTMLElement>();
  const chip = make('div', 'pz-focus-chip');
  chip.setAttribute?.('aria-hidden', 'true');
  chip.hidden = true;
  let chipMounted = false;

  let activeEl: HTMLElement | undefined;
  let activeOptionId: string | undefined;
  /** True while what is highlighted is a suggestion rather than a real focus. */
  let suggesting = false;
  let frame: number | undefined;
  let frameFallback: number | undefined;
  let blurTimer: number | undefined;
  const stops: Array<() => void> = [];

  /** `addEventListener` against anything that has one, remembered for `destroy`. */
  function on(target: unknown, type: string, handler: (event: Event) => void, opts?: unknown): void {
    const node = target as
      | {
          addEventListener?: (t: string, h: unknown, o?: unknown) => void;
          removeEventListener?: (t: string, h: unknown, o?: unknown) => void;
        }
      | undefined;
    if (!node?.addEventListener) return;
    node.addEventListener(type, handler, opts);
    stops.push(() => node.removeEventListener?.(type, handler, opts));
  }

  function mountChip(): void {
    if (chipMounted) return;
    options.previewBox?.appendChild?.(chip);
    chipMounted = true;
  }

  function setActive(hit: OptionHit | null): void {
    if (hit && hit.optionId === activeOptionId && hit.el === activeEl) return;
    if (activeEl) activeEl.classList?.remove('is-active-question');
    if (!hit) {
      activeEl = undefined;
      activeOptionId = undefined;
      clear();
      return;
    }
    activeEl = hit.el;
    activeOptionId = hit.optionId;
    hit.el.classList?.add('is-active-question');
    schedule();
  }

  function clear(): void {
    for (const box of spots.values()) box.remove?.();
    spots.clear();
    chip.hidden = true;
    stopFrame();
  }

  function stopFrame(): void {
    if (frame !== undefined && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(frame);
    frame = undefined;
    if (frameFallback !== undefined) {
      window.clearTimeout?.(frameFallback);
      frameFallback = undefined;
    }
  }

  /** Coalesce resize/paint/scroll work, like guide.ts's own `schedule()`. */
  function schedule(): void {
    if (frame !== undefined || frameFallback !== undefined) return;
    const run = (): void => {
      stopFrame();
      place();
    };
    if (typeof requestAnimationFrame === 'function') {
      frame = requestAnimationFrame(run);
      frameFallback = window.setTimeout?.(run, 120) as unknown as number;
    } else {
      frameFallback = window.setTimeout?.(run, 16) as unknown as number;
    }
  }

  function place(): void {
    if (!activeOptionId) return;
    const canvasRect = rectOf(options.canvas);
    // A collapsed strip or full-screen inspection should show no highlight.
    if (canvasRect.height < 80 || safe(() => options.canvasInLightbox(), false)) {
      for (const box of spots.values()) box.hidden = true;
      chip.hidden = true;
      return;
    }
    mountChip();
    const scene = safe(() => options.scene(), undefined);
    const values = safe(() => options.values(), {});
    const ids = spotElementsFor(options.product, scene, values, activeOptionId);
    const visible = new Set<string>();
    /** The first outlined layer, in the containing block's coordinates — the chip hangs off it. */
    let firstGeometry: GuideSpot | undefined;
    for (const id of ids) {
      let box = spots.get(id);
      if (!box) {
        box = make('div', 'pz-focus-spot');
        box.setAttribute?.('aria-hidden', 'true');
        options.previewBox?.appendChild?.(box);
        spots.set(id, box);
      }
      // Mobile wizard styles make the preview position:static; its actual
      // containing block can then be a scrollable ancestor of the preview —
      // the same `offsetParent` trick guide.ts uses.
      const geometry = spotGeometry(id, box.offsetParent as HTMLElement | null, canvasRect, scene);
      if (!geometry) {
        box.hidden = true;
        continue;
      }
      visible.add(id);
      box.hidden = false;
      paint(box, geometry);
      firstGeometry ??= geometry;
    }
    for (const [id, box] of spots) {
      if (!visible.has(id)) {
        box.remove?.();
        spots.delete(id);
      }
    }
    const option = options.product.options?.find((o) => o.id === activeOptionId);
    chip.textContent = chipText(options.texts, option?.label ?? '', suggesting ? 'add' : 'editing');
    // The chip is a label ON the outlined layer — just above it, or just below
    // when the layer touches the top of the picture — so "Dodaj: Ime" points at
    // the line that will carry the name. Anchored to the picture rather than
    // to the preview box: in the wizard the box is position:static inside a
    // sticky pane that also holds the share button. Shown BEFORE measuring —
    // a hidden chip has no offsetParent and no width.
    chip.hidden = false;
    chip.style?.setProperty?.('max-width', `${Math.max(80, canvasRect.width - 16)}px`);
    const anchor = (chip.offsetParent as HTMLElement | null) ?? options.previewBox;
    const anchorRect = rectOf(anchor);
    const scrollLeft = (anchor as { scrollLeft?: number }).scrollLeft || 0;
    const scrollTop = (anchor as { scrollTop?: number }).scrollTop || 0;
    const canvasLeft = canvasRect.left - anchorRect.left + scrollLeft;
    const canvasTop = canvasRect.top - anchorRect.top + scrollTop;
    const chipHeight = Math.max(20, (chip as { offsetHeight?: number }).offsetHeight || 24);
    const chipWidth = Math.max(60, (chip as { offsetWidth?: number }).offsetWidth || 120);
    const clamp = (value: number, low: number, high: number): number =>
      Math.min(Math.max(value, low), Math.max(low, high));
    let left = canvasLeft + 8;
    // With no layer to point at, the bottom-left corner — away from the photo
    // controls that live along the top of a picture.
    let top = canvasTop + canvasRect.height - chipHeight - 8;
    if (firstGeometry) {
      left = clamp(firstGeometry.left, canvasLeft + 4, canvasLeft + canvasRect.width - chipWidth - 4);
      const above = firstGeometry.top - chipHeight - 6;
      top =
        above >= canvasTop + 4
          ? above
          : clamp(
              firstGeometry.top + firstGeometry.height + 6,
              canvasTop + 4,
              canvasTop + canvasRect.height - chipHeight - 4,
            );
    }
    chip.style?.setProperty?.('left', `${left}px`);
    chip.style?.setProperty?.('top', `${top}px`);
  }

  function paint(box: HTMLElement, geometry: GuideSpot): void {
    const style = box.style;
    style?.setProperty?.('left', `${geometry.left}px`);
    style?.setProperty?.('top', `${geometry.top}px`);
    style?.setProperty?.('width', `${geometry.width}px`);
    style?.setProperty?.('height', `${geometry.height}px`);
    style?.setProperty?.('transform', `rotate(${geometry.rotation}deg)`);
  }

  function spotGeometry(
    elementId: string,
    containingBlock: HTMLElement | null,
    canvasRect: Box,
    scene: Scene | undefined,
  ): GuideSpot | null {
    const anchor = containingBlock ?? options.previewBox;
    const boxRect = rectOf(anchor);
    return elementSpot(scene, elementId, canvasRect, {
      left: boxRect.left + ((anchor as { clientLeft?: number }).clientLeft || 0) -
        ((anchor as { scrollLeft?: number }).scrollLeft || 0),
      top: boxRect.top + ((anchor as { clientTop?: number }).clientTop || 0) -
        ((anchor as { scrollTop?: number }).scrollTop || 0),
    });
  }

  // ── Listening to the form ───────────────────────────────────────────────

  function enter(event: Event): void {
    const hit = optionOfNode((event as { target?: unknown }).target);
    if (!hit) return;
    if (blurTimer !== undefined) {
      window.clearTimeout?.(blurTimer);
      blurTimer = undefined;
    }
    // A real touch of the form always beats a suggestion — including when it
    // lands on the very question being suggested, where `setActive` has nothing
    // to change but the chip still has to stop saying "Dodaj".
    const wasSuggesting = suggesting;
    suggesting = false;
    setActive(hit);
    if (wasSuggesting) schedule();
  }

  /** The question the cursor is in right now, if any. */
  function focusedOption(): string | undefined {
    const active = safe(() => layers.activeElementDeep(), null);
    return optionOfNode(active)?.optionId ?? undefined;
  }

  /**
   * The outermost `[data-option="id"]` inside the form — the question's row,
   * not the narrower song line nested in it. Walked by hand rather than through
   * `querySelector`, so an option id with a quote in it cannot break a selector.
   */
  function rowOf(optionId: string): HTMLElement | null {
    const walk = (node: HTMLElement | null, depth: number): HTMLElement | null => {
      if (!node || depth > 16) return null;
      const kids = (node as unknown as { children?: ArrayLike<HTMLElement> }).children;
      for (let i = 0; i < (kids?.length ?? 0); i += 1) {
        const child = kids![i];
        if (!child) continue;
        if (child.getAttribute?.('data-option') === optionId) return child;
        const found = walk(child, depth + 1);
        if (found) return found;
      }
      return null;
    };
    return safe(() => walk(options.form, 0), null);
  }

  function suggest(optionId: string | null, opts?: { onlyWhenIdle?: boolean }): void {
    const focused = focusedOption();
    if (opts?.onlyWhenIdle && focused !== undefined) return;
    // A question that genuinely has the cursor keeps the highlight.
    if (!suggesting && activeOptionId !== undefined && focused === activeOptionId) return;
    if (optionId === null) {
      if (suggesting) {
        suggesting = false;
        setActive(null);
      }
      return;
    }
    const el = rowOf(optionId);
    if (!el) return;
    suggesting = true;
    setActive({ el, optionId });
    // `setActive` is a no-op when the same row is already highlighted; the chip
    // may still need its leading word rewritten.
    schedule();
  }

  on(options.form, 'focusin', enter);
  on(options.form, 'pointerdown', enter);
  on(options.form, 'focusout', () => {
    if (blurTimer !== undefined) window.clearTimeout?.(blurTimer);
    blurTimer = window.setTimeout?.(() => {
      blurTimer = undefined;
      // A suggestion never had focus, so losing focus cannot take it away —
      // otherwise a shopper who tabs through and out of the form would be left
      // with nothing pointing at the question they still have to answer.
      if (suggesting) return;
      // Tabbing between two controls of the same question (a swatch's own
      // buttons, a song line's search box and its clear button) must not
      // flicker the highlight off and straight back on.
      const active = safe(() => layers.activeElementDeep(), null);
      const hit = optionOfNode(active);
      if (hit && hit.optionId === activeOptionId) return;
      setActive(null);
    }, 150) as unknown as number;
  });

  on(typeof window === 'undefined' ? undefined : window, 'resize', () => schedule());
  on(
    typeof window === 'undefined' ? undefined : window,
    'scroll',
    () => schedule(),
    { passive: true, capture: true },
  );
  stops.push(safe(() => layers.observeSize(options.canvas, () => schedule()), () => undefined));

  return {
    relayout(): void {
      schedule();
    },
    suggest,
    clear,
    destroy(): void {
      clear();
      suggesting = false;
      if (blurTimer !== undefined) window.clearTimeout?.(blurTimer);
      blurTimer = undefined;
      for (const release of stops.splice(0, stops.length)) safe(release, undefined);
      if (activeEl) activeEl.classList?.remove('is-active-question');
      activeEl = undefined;
      activeOptionId = undefined;
      chip.remove?.();
      chipMounted = false;
    },
  };
}
